import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { HISTORY } from "@/constants/appIds";
import { formatDateTimeJakarta, formatDate, formatRupiah, STATUS_META } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ReceiptThumb } from "@/components/ReceiptImage";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Eye } from "lucide-react";

export default function History() {
  const nav = useNavigate();
  const [status, setStatus] = useState("all");
  const [outlet, setOutlet] = useState("all");
  const [category, setCategory] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");

  const { data: outlets = [] } = useQuery({ queryKey: ["outlets"], queryFn: async () => (await api.get("/outlets")).data });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data = [], isLoading } = useQuery({
    queryKey: ["history"],
    queryFn: async () => (await api.get("/receipts")).data,
  });

  const filtered = data.filter((r) => {
    if (status !== "all" && r.status !== status) return false;
    if (outlet !== "all" && r.outlet_id !== outlet) return false;
    if (category !== "all" && r.category !== category) return false;
    const td = r.corrected?.transaction_date;
    if (dateFrom && (!td || td < dateFrom)) return false;
    if (dateTo && (!td || td > dateTo)) return false;
    if (q && !((r.corrected?.supplier || "").toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const resetFilters = () => { setStatus("all"); setOutlet("all"); setCategory("all"); setDateFrom(""); setDateTo(""); setQ(""); };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/daily-report/export?filled=true`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Daily_Report_terisi.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success("File Daily Report (terisi) diunduh.");
    } catch {
      toast.error("Gagal mengunduh. Pastikan ada data disetujui.");
    }
  };

  return (
    <div className="space-y-6 nh-fade">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Riwayat Belanja</h1>
          <p className="text-sm text-muted-foreground">Arsip nota dengan foto asli, status, dan jejak persetujuan.</p>
        </div>
        <Button onClick={exportExcel} data-testid={HISTORY.export} variant="outline" className="rounded-full">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Unduh Daily Report (.xlsx)
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap items-end">
        <Input placeholder="Cari supplier…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs h-9" data-testid="history-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 h-9" data-testid={HISTORY.statusFilter}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="pending_review">Menunggu</SelectItem>
            <SelectItem value="draft">Draf</SelectItem>
            <SelectItem value="approved">Disetujui</SelectItem>
            <SelectItem value="rejected">Ditolak</SelectItem>
          </SelectContent>
        </Select>
        <Select value={outlet} onValueChange={setOutlet}>
          <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Outlet</SelectItem>
            {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44 h-9" data-testid="history-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div>
          <label className="text-[10px] text-muted-foreground block">Dari tanggal</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" data-testid="history-date-from" />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground block">Sampai tanggal</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" data-testid="history-date-to" />
        </div>
        <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9" data-testid="history-reset">Reset</Button>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">{filtered.length} nota ditemukan (transaksi berdasarkan tanggal nota).</p>

      <Card className="overflow-hidden" data-testid={HISTORY.table}>
        <div className="divide-y divide-border">
          {isLoading && <div className="p-8 text-center text-muted-foreground text-sm">Memuat…</div>}
          {!isLoading && filtered.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">Tidak ada data.</div>}
          {filtered.map((r) => (
            <div key={r.id} data-testid={HISTORY.row} className="flex items-center gap-3 p-3 hover:bg-secondary/40 transition-colors">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-secondary shrink-0">
                {r.photos?.[0] && <ReceiptThumb path={r.photos[0].storage_path} className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium truncate">{r.corrected?.supplier || "Supplier belum terbaca"}</p>
                  <Badge variant="outline" className={`text-[10px] ${STATUS_META[r.status]?.cls}`}>{STATUS_META[r.status]?.label}</Badge>
                  {r.daily_report_status === "recorded_internal" && (
                    <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Daily Report ✓ {r.daily_report_ref}</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {r.outlet_name} · Tgl transaksi {formatDate(r.corrected?.transaction_date)} · Unggah {formatDateTimeJakarta(r.uploaded_at)}
                </p>
              </div>
              <span className="font-mono text-sm hidden sm:block">{formatRupiah(r.totals?.computed_total)}</span>
              <Button size="icon" variant="ghost" onClick={() => nav(`/review/${r.id}`)}><Eye className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
